import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState(!token);

  useEffect(() => {
    if (!token) setTokenError(true);
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Şifre en az 8 karakter olmalı.");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Şifreler eşleşmiyor.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(data?.message || "Şifreniz güncellendi.");
      navigate("/giris", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400) {
        toast.error(detail || "Bağlantı geçersiz veya süresi dolmuş.");
      } else if (!err?.response) {
        toast.error("Sunucuya bağlanılamadı. Lütfen birkaç saniye sonra tekrar deneyin.");
      } else {
        toast.error(detail || "Şifre güncellenemedi.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute top-6 left-6"><Link to="/"><Logo /></Link></div>
      <div className="w-full max-w-md relative z-10">
        <div className="glass-strong rounded-3xl p-8" data-testid="reset-password-page">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-4">
            <KeyRound size={20} className="text-cyan-300" strokeWidth={1.6} />
          </div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Yeni şifre belirleyin</h1>
          <p className="text-slate-400 text-sm mb-7 leading-relaxed">
            Yeni şifrenizi belirleyin. En az 8 karakter olmalı.
          </p>

          {tokenError ? (
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-5 text-sm text-red-100 leading-relaxed" data-testid="reset-token-missing">
              Bağlantı geçersiz. Lütfen "Şifremi unuttum" akışını tekrar başlatın.
              <div className="mt-4">
                <Link to="/sifremi-unuttum" className="text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                  Yeniden talep et
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Yeni şifre</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="En az 8 karakter"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Yeni şifre tekrar</label>
                <input
                  type="password"
                  required
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="mt-2 w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Şifreyi tekrar girin"
                  data-testid="reset-password-confirm"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="neon-btn w-full text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="reset-submit"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Şifreyi güncelle
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link to="/giris" className="text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1">
              <ArrowLeft size={13} /> Giriş ekranına dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
